import { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ARENA_NAV_ITEMS } from 'modules/arena/dashboard/content';
import { SAAS_PLANS } from 'modules/arena/onboarding/content';
import { useArenaProfile } from 'shared/contexts/ArenaProfileContext';
import { Badge } from 'shared/components/ui/Badge';
import { SPRING } from 'shared/components/motion/tokens';
import { cn } from 'shared/utils/cn';

export function ArenaSidebar() {
  const navRef = useRef<HTMLElement>(null);
  const { profile } = useArenaProfile();
  const plan = SAAS_PLANS.find((item) => item.id === profile.planId) ?? SAAS_PLANS[1];
  const { pathname } = useLocation();

  // Celular: centraliza a aba ativa na faixa. Mexe só no scroll horizontal da faixa
  // (scrollIntoView rolaria a página inteira também).
  useEffect(() => {
    const nav = navRef.current;
    const active = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !active || nav.scrollWidth <= nav.clientWidth) return;
    nav.scrollTo({ left: active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2, behavior: 'smooth' });
  }, [pathname]);

  return (
    <aside className="flex w-full flex-col px-4 py-3 md:p-4">
      <div>
        <p className="text-sm font-bold text-ink">
          {profile.arenaName} <span className="font-normal text-ink-secondary md:hidden">· {profile.unitName}</span>
        </p>
        <p className="mt-1 hidden text-xs text-ink-secondary md:block">{profile.unitName}</p>

        {/* Celular: faixa de abas que rola para o lado. Desktop: menu vertical. */}
        <nav ref={navRef} className="no-scrollbar relative -mx-4 mt-3 flex gap-1 overflow-x-auto px-4 text-sm md:mx-0 md:mt-6 md:flex-col md:overflow-visible md:px-0">
          {ARENA_NAV_ITEMS.map((item) =>
            item.path ? (
              <NavLink
                key={item.id}
                to={item.path}
                end
                className={({ isActive }) =>
                  cn(
                    'relative flex flex-shrink-0 items-center justify-between gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-left transition-colors duration-200',
                    isActive ? 'font-medium text-primary' : 'text-ink-secondary hover:bg-chip hover:text-ink'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {/* O destaque é um único elemento que desliza de um item para o outro. */}
                    {isActive && (
                      <motion.span
                        layoutId="arena-nav-active"
                        transition={SPRING}
                        className="absolute inset-0 rounded-lg bg-accent-soft/70"
                      >
                        <span className="absolute inset-y-2 left-0 w-1 rounded-full bg-primary" />
                      </motion.span>
                    )}
                    <span className="relative">{item.label}</span>
                    {item.badge && (
                      <Badge tone="accent" className="relative">
                        {item.badge}
                      </Badge>
                    )}
                  </>
                )}
              </NavLink>
            ) : (
              <button
                key={item.id}
                type="button"
                disabled
                className="flex cursor-not-allowed items-center justify-between rounded-lg px-3 py-2 text-left text-ink-secondary/50"
                title="Em breve"
              >
                {item.label}
                {item.badge && <Badge tone="accent">{item.badge}</Badge>}
              </button>
            )
          )}
        </nav>
      </div>

      <div className="mt-6 hidden rounded-xl border border-border/60 bg-background p-3 text-xs md:block">
        <p className="font-medium text-ink">Plano SaaS</p>
        <p className="text-ink-secondary">
          {plan.name} · R$ {plan.price}/mês + 2%
        </p>
        <p className="mt-1 flex items-center gap-1 text-ink-secondary">
          <span className="h-1.5 w-1.5 rounded-full bg-success" /> Split e subconta ativos
        </p>
      </div>
    </aside>
  );
}
